import React, { useEffect, useRef, useState } from 'react'
import { Form, InputNumber, Loader, Message, Panel } from 'rsuite'
import { useTranslation } from 'react-i18next'
import { Capabilities, hasPermission } from '@/shared'
import SettingsModel, { TTenantSettings } from '@client/models/settings'
import toastMessage from '@client/utils/toastMessage'

//
// Settings that apply to the whole tenant.
//
// Readable by anyone signed in — the values describe how the application behaves
// for them — but only editable with settings_write, which administrators and
// owners hold. The form renders read-only for everyone else rather than hiding,
// so a project member can see what applies to them.
//

const settingsModel = new SettingsModel()

export default function TenantSettings() {
    const { t } = useTranslation()
    const [settings, setSettings] = useState<TTenantSettings | null>(null)
    const [error, setError] = useState('')
    // What the server last confirmed. Leaving the field only writes when the
    // value actually differs, so tabbing through does not save.
    const savedRef = useRef<TTenantSettings | null>(null)

    const readOnly = !hasPermission(Capabilities.settings_write)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            const res = await settingsModel.read()
            if (!cancelled && res?.data) {
                setSettings(res.data)
                savedRef.current = res.data
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    /**
     * Saves shortly after the last keystroke — there is no save button.
     *
     * Not on blur: rsuite's InputNumber does not pass onBlur down to the input
     * it renders, so leaving the field never fired. A short delay also avoids
     * writing once per digit while somebody types "120".
     *
     * On failure the field goes back to the last confirmed value, so what is on
     * screen is never something the server refused.
     */
    const save = async (value: number) => {
        if (readOnly) return
        if (value === savedRef.current?.taskRetentionMinutes) return

        setError('')
        try {
            const res = await settingsModel.update({ taskRetentionMinutes: value })
            if (res?.data) {
                setSettings(res.data)
                savedRef.current = res.data
                toastMessage(t('Saved.'))
            }
        } catch (err: any) {
            // The server rejects a value outside its range rather than adjusting
            // it, so there is something worth showing.
            setError(err?.response?.data?.message || err?.message || t('Could not save settings.'))
            if (savedRef.current) setSettings(savedRef.current)
        }
    }

    // Debounced write. The timer is cleared on every change, so only the value
    // somebody settles on reaches the server.
    useEffect(() => {
        if (!settings) return
        const value = settings.taskRetentionMinutes
        if (!Number.isFinite(value) || value === savedRef.current?.taskRetentionMinutes) return

        const timer = setTimeout(() => save(value), 700)
        return () => clearTimeout(timer)
    }, [settings?.taskRetentionMinutes])

    if (!settings) return <Loader content={t('Loading...')} />

    return (
        <Panel bordered header={t('Tenant settings')}>
            {error && (
                <Message showIcon type="error" style={{ marginBottom: '1rem' }}>
                    {error}
                </Message>
            )}

            <Form fluid>
                <Form.Group controlId="taskRetentionMinutes">
                    <Form.ControlLabel>{t('Keep finished tasks for (minutes)')}</Form.ControlLabel>
                    <Form.HelpText>
                        {t('A project can set its own value; without one it uses this.')}
                    </Form.HelpText>
                    <InputNumber
                        name="taskRetentionMinutes"
                        min={1}
                        max={1440}
                        disabled={readOnly}
                        style={{ width: '7rem' }}
                        value={settings.taskRetentionMinutes}
                        onChange={(value) =>
                            setSettings({ ...settings, taskRetentionMinutes: Number(value) })
                        }
                    />
                </Form.Group>
            </Form>
        </Panel>
    )
}
